import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { KeyRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, FormEvent, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import useConfirmSignUp from 'v2/core/services/auth/useConfirmSignUp';
import useResendOtp from 'v2/core/services/auth/useResendOtp';
import useSignIn from 'v2/core/services/auth/useSignIn';
import { RegisterState } from 'v2/views/layouts/authenticationModule/authTypes';

interface RegisterWithVerificationViewProps {
  userPayload: RegisterState;
}

export const RegisterWithVerificationView = ({
  userPayload,
}: RegisterWithVerificationViewProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [code, setCode] = useState('');

  const { signIn, loader: signInLoader } = useSignIn({
    ...userPayload,
  });

  const { showSuccessNotification } = useNotificationContext();

  const [confirmSignUp, confirmStatus] = useConfirmSignUp({
    userPayload,
    code,
    onSuccess: (e: FormEvent) => signIn(e),
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  const [resendOTP, resendOTPStatus] = useResendOtp({
    email: userPayload.email,
    onSuccess: () => {
      showSuccessNotification({
        contentComponent: 'Verification code has been sent succesfully.',
        props: {},
      });
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;

    setCode(value);
  };

  return (
    <>
      <Box pb={2.5}>
        <Typography>Verification code sent to your registered email</Typography>
      </Box>
      <Box component="form" onSubmit={confirmSignUp}>
        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}
        <Stack spacing={2}>
          <TextField
            onChange={handleOnChange}
            label="Verification Code"
            placeholder="Enter verification code"
            value={code}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyRounded color="disabled" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              pattern: '^[0-9]*$',
            }}
          />
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            type="submit"
            loading={IsLoading(confirmStatus) || signInLoader}
            disabled={IsLoading(confirmStatus) || signInLoader}
          >
            Verify
          </LoadingButton>
        </Stack>
      </Box>
      <Stack pt={1} direction="row" justifyContent="flex-end">
        <LoadingButton
          loading={IsLoading(resendOTPStatus)}
          onClick={resendOTP}
          size="small"
          variant="text"
        >
          Resend OTP
        </LoadingButton>
      </Stack>
    </>
  );
};
